import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';

import { AuthService } from './auth.service';
import { RouteValidationService } from './route-validation.service';
import { SafeLoggerService } from './safe-logger.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuardService implements CanActivate {
  constructor(
    private auth_$: AuthService,
    private router: Router,
    private routeValidation_$: RouteValidationService,
    private safeLogger: SafeLoggerService
  ) { }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return new Promise(resolve => {
      this.safeLogger.log('AuthGuard: Checking route:', route.routeConfig?.path, 'state:', state.url);

      // Special check: Protect any route starting with /patient-overview/ even if it doesn't match a specific route
      // This catches routes like /patient-overview/:caseName/sender/:facilityID/retreive/images
      // that might fall through to the catch-all route
      if (state.url.startsWith('/patient-overview/') && !route.routeConfig) {
        this.safeLogger.log('AuthGuard: Detected patient-overview path without route config, protecting it');
        // This will be handled by the auth check below
      }

      // Check if we're on a patient-overview route - these might need extra time for auth state to restore
      const isPatientOverviewRoute = state.url.includes('patient-overview-modal');
      const hasKeycloakTokens = sessionStorage.getItem('keycloak_has_tokens') === 'true';

      // For patient-overview routes with Keycloak tokens, wait for auth state with retries
      if (isPatientOverviewRoute && hasKeycloakTokens) {
        this.safeLogger.log('AuthGuard: Patient-overview route with Keycloak tokens, waiting for auth state...');

        // Wait for auth state with retries
        let retries = 0;
        const maxRetries = 10;
        const retryDelay = 200;

        const checkAuthState = async () => {
          while (retries < maxRetries) {
            const isSignedIn = this.auth_$.userIsSignedIn.getValue();
            if (isSignedIn) {
              this.safeLogger.log('AuthGuard: User signed in after waiting (attempt', retries, ')');
              handleAuthResult(true);
              return;
            }

            await new Promise(resolve => setTimeout(resolve, retryDelay));
            retries++;
          }

          // After max retries, if Keycloak tokens exist, allow access
          if (hasKeycloakTokens) {
            this.safeLogger.log('AuthGuard: Auth state not ready after waiting, but Keycloak tokens exist - allowing access');
            handleAuthResult(true);
          } else {
            handleAuthResult(false);
          }
        };

        const handleAuthResult = (isSignedIn: boolean) => {
          if (isSignedIn) {
            this.safeLogger.log('You are logged in (AuthGuard)');
            resolve(true);
          } else {
            // Validate the state.url before using it as referer to prevent open redirects
            const referer = this.routeValidation_$.isValidInternalRoute(state.url)
              ? state.url
              : '/';

            const hint = route.queryParams['hint'] || null;
            resolve(
              this.router.createUrlTree(['/login'], {
                queryParams: hint ? { referer, hint } : { referer }
              })
            );
          }
        };

        checkAuthState();
        return;
      }

      // Normal flow: if already signed in, allow immediately.
      // Otherwise wait up to 2s for Firebase auth to initialize before
      // concluding the user is definitely unauthenticated (take(1) on a
      // BehaviorSubject<false> fires before the Firebase auth state resolves
      // on direct URL navigation, causing a spurious redirect to /login).
      if (this.auth_$.userIsSignedIn.getValue()) {
        this.safeLogger.log('AuthGuard: User signed in: true');
        resolve(true);
        return;
      }

      let retries = 0;
      const maxRetries = 10;
      const retryDelay = 200;

      const waitForAuth = async () => {
        while (retries < maxRetries) {
          await new Promise(r => setTimeout(r, retryDelay));
          retries++;
          const isSignedIn = this.auth_$.userIsSignedIn.getValue();
          this.safeLogger.log('AuthGuard: User signed in:', isSignedIn, '(attempt', retries, ')');
          if (isSignedIn) {
            resolve(true);
            return;
          }
        }

        // Auth definitively not ready — redirect to login
        const referer = this.routeValidation_$.isValidInternalRoute(state.url)
          ? state.url
          : '/';
        const hint = route.queryParams['hint'] || null;
        resolve(
          this.router.createUrlTree(['/login'], {
            queryParams: hint ? { referer, hint } : { referer }
          })
        );
      };

      waitForAuth();
    });
  }
}
